import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  Box,
  IconButton,
  Grid,
  Paper,
  TextField,
  MenuItem,
  InputAdornment,
  Avatar
} from "@mui/material";

import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Visibility,
  VisibilityOff,
  AddAPhoto as AddAPhotoIcon
} from "@mui/icons-material";
import { MuiTelInputInfo, matchIsValidTel } from "mui-tel-input";
import { toast } from "react-toastify";
import CustomTextField from "../../components/ui/Textfield";
import CustomSelect from "../../components/ui/Select";
import PhoneNumberField from "../../components/ui/PhoneNumberField";

interface UserFormData {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  user_type: "company_user" | "individual" | "visitor";
  role: string;
  phone_number: string;
  password: string;
  is_active: boolean;
  profile_picture: File | null;
  profile_picture_url: string | null;
}

interface AddUserFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (userData: FormData) => void; // Changed to FormData for file upload
}

export default function AddUserForm({
  open,
  onClose,
  onSave,
}: AddUserFormProps) {
  // const userRoles = [
  //   { title: "Admin", value: "admin" },
  //   { title: "Staff", value: "staff" },
  //   { title: "Crew", value: "crew" },
  //   { title: "Client", value: "client" },
  //   { title: "Funeral Director", value: "funeral_director" }
  // ];

  const [formData, setFormData] = useState<UserFormData>({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    phone_number: "",
    user_type: "company_user",
    role: "client",
    password: "",
    is_active: true,
    profile_picture: null,
    profile_picture_url: null,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const rolesList = [
    { value: "client", label: "Client" },
    { value: "funeral_director", label: "Funeral Director" },
    { value: "staff", label: "Staff" },
    { value: "admin", label: "Admin" },
    { value: "crew", label: "Crew" },
    { value: "dev", label: "Dev" },
    { value: "referral", label: "Referral" },
  ];

  // Sync username with email whenever email changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      username: prev.email,
    }));
  }, [formData.email]);

  // Handle file preview
  useEffect(() => {
    if (formData.profile_picture) {
      const url = URL.createObjectURL(formData.profile_picture);
      setPreviewUrl(url);
      
      // Clean up URL when component unmounts or file changes
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [formData.profile_picture]);

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleInputChange =
    (field: keyof UserFormData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
          ...prev,
          [field]: event.target.value,
        }));
      };

  const handleRoleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    setFormData((prev) => ({
      ...prev,
      role: String(event.target.value) as UserFormData["role"],
    }));
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setFormData((prev) => ({
      ...prev,
      password: newPassword,
    }));
  };

  const handleUserTypeChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    setFormData((prev) => ({
      ...prev,
      user_type: String(event.target.value) as UserFormData["user_type"],
    }));
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      is_active: event.target.checked,
    }));
  };

  const handlePhoneChange = (_value: string, info: MuiTelInputInfo) => {
    setFormData((prev) => ({
      ...prev,
      phone_number: `+${info.countryCallingCode}${info.nationalNumber}`,
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPG, PNG, or GIF).');
        return;
      }
      
      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('File size too large. Maximum size is 5MB.');
        return;
      }
      
      setFormData((prev) => ({
        ...prev,
        profile_picture: file,
      }));
    }
  };

  const handleSave = () => {
    if (!formData.phone_number) {
      toast.error("Please provide a phone number for this user.");
      return;
    }

    if (!matchIsValidTel(formData.phone_number)) {
      toast.error("Please enter a valid phone number with country code.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('email', formData.email);
    formDataToSend.append('username', formData.username);
    formDataToSend.append('first_name', formData.first_name);
    formDataToSend.append('last_name', formData.last_name);
    formDataToSend.append('user_type', formData.user_type);
    formDataToSend.append('role', formData.role);
    formDataToSend.append('phone_number', formData.phone_number);
    formDataToSend.append('is_active', formData.is_active ? 'true' : 'false');
    formDataToSend.append('company_id', '1');
    const finalPassword = formData.password || generatePassword();
    formDataToSend.append('password', finalPassword);
        if (formData.profile_picture instanceof File) {
          formDataToSend.append('profile_picture', formData.profile_picture);
    }
    onSave(formDataToSend);
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      username: "",
      email: "",
      phone_number: "",
      user_type: "company_user",
      role: "admin",
      password: "",
      is_active: true,
      profile_picture: null,
      profile_picture_url: null,
    });
    setPreviewUrl(null);
    setPasswordError("");
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Function to convert file to base64 (alternative method)
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" component="div">
            Add New User
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          {/* Basic Information Section */}
          <Box mb={3}>
            <Typography variant="h6" sx={{ fontSize: 16, mb: 2 }}>
              Basic Information
            </Typography>

            {/* Upload Section */}
            <Box
              mb={3}
              alignItems={"center"}
              display="flex"
              justifyContent="center"
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  textAlign: "center",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px dashed",
                  borderColor: "grey.300",
                  backgroundColor: "grey.50",
                  cursor: "pointer",
                  borderRadius: "50%",
                  width: 150,
                  height: 150,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: "grey.100",
                    borderColor: "primary.main",
                  },
                }}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileUpload}
                />
                
                {previewUrl ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Avatar
                      src={previewUrl}
                      alt="Profile preview"
                      sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.2s",
                        "&:hover": {
                          opacity: 1,
                        },
                      }}
                    >
                      <AddAPhotoIcon sx={{ color: "white" }} />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={1}
                  >
                    <CloudUploadIcon color="action" fontSize="large" />
                    <Typography variant="body2" color="text.secondary">
                      Click to upload picture
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      JPG, PNG, GIF (max 5MB)
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>

            {formData.profile_picture && (
              <Box mb={2} textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Selected: {formData.profile_picture.name}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, profile_picture: null }));
                    setPreviewUrl(null);
                  }}
                  sx={{ ml: 1 }}
                >
                  Remove
                </Button>
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <CustomTextField
                  fullWidth
                  label="First Name"
                  variant="outlined"
                  size="small"
                  value={formData.first_name}
                  onChange={handleInputChange("first_name")}
                  required
                />
              </Grid>

              <Grid size={{ xs: 6 }}>
                <CustomTextField
                  fullWidth
                  label="Surname"
                  variant="outlined"
                  size="small"
                  value={formData.last_name}
                  onChange={handleInputChange("last_name")}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  fullWidth
                  label="Username"
                  variant="outlined"
                  size="small"
                  value={formData.username}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  size="small"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange("email")}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <PhoneNumberField
                  fullWidth
                  label="Phone Number"
                  placeholder="Phone Number. e.g. +263712345678"
                  value={formData.phone_number || ""}
                  onChange={handlePhoneChange}
                  defaultCountry="ZW"
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>

          {/* User Type Section */}
          <Box mb={3}>
            <FormControl fullWidth>
              <CustomSelect
                labelId="user-type-select-label"
                value={formData.user_type}
                label="User Type"
                onChange={handleUserTypeChange}
              >
                <MenuItem value="company_user">Company</MenuItem>
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="visitor">Visitor</MenuItem>
              </CustomSelect>
            </FormControl>
          </Box>

          {/* Role Section */}
          <Box mb={3}>
            <FormControl fullWidth>
              <CustomSelect
                labelId="role-select-label"
                value={formData.role}
                label="Role"
                onChange={handleRoleChange}
              >
                {rolesList.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          </Box>

          {/* Password Section - Hidden as per original code */}
          <Box mb={3} sx={{ display: "none" }}>
            <Typography variant="h6" sx={{ fontSize: 16, mb: 2 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              label="Password"
              variant="outlined"
              size="small"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handlePasswordChange}
              error={!!passwordError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Status and Verification Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontSize: 16, mb: 2 }}>
              Status & Verification
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <FormLabel component="legend">Account Status</FormLabel>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={handleStatusChange}
                        color="primary"
                      />
                    }
                    label={formData.is_active ? "Active" : "Inactive"}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: "primary.main",
              textTransform: "none",
            }}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}